import { useState, useEffect, useMemo } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { PaperPlaneTilt, ShieldCheck, Cube, MagnifyingGlass, Clock, CheckCircle, XCircle, Key, SpinnerGap, CaretRight, Warning, Prohibit, Lock, Check, Users, User, CaretUpDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';

export default function AccessRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [catalog, setCatalog] = useState({ roles: [], applications: [] });
  const [allRoles, setAllRoles] = useState([]);
  const [allEntitlements, setAllEntitlements] = useState([]);
  const [allIdentities, setAllIdentities] = useState([]);
  const [sodProtectedRoles, setSodProtectedRoles] = useState(new Set());
  const [userRoles, setUserRoles] = useState(new Set()); // Roles the user already has
  const [pendingRoles, setPendingRoles] = useState(new Set()); // Roles with pending requests
  const [showCatalog, setShowCatalog] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showAppRolesDialog, setShowAppRolesDialog] = useState(false);
  const [showSodBlockedDialog, setShowSodBlockedDialog] = useState(false);
  const [showEntitlementsDialog, setShowEntitlementsDialog] = useState(false);
  const [selectedRoleForEntitlements, setSelectedRoleForEntitlements] = useState(null);
  const [sodConflict, setSodConflict] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appRoles, setAppRoles] = useState([]);
  const [loadingAppRoles, setLoadingAppRoles] = useState(false);
  const [checkingSod, setCheckingSod] = useState(false);
  const [justification, setJustification] = useState('');
  const [search, setSearch] = useState('');
  const [catalogTab, setCatalogTab] = useState('all'); // all, roles, apps
  const [requestForSomeoneElse, setRequestForSomeoneElse] = useState(false);
  const [selectedIdentityId, setSelectedIdentityId] = useState('');
  const [identitySearch, setIdentitySearch] = useState('');
  const [identityPopoverOpen, setIdentityPopoverOpen] = useState(false);

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    return `${API.defaults.baseURL}/applications/logo/${logoPath}`;
  };

  useEffect(() => {
    loadRequests();
    loadCatalog();
    loadSodProtectedRoles();
    loadEntitlements();
    loadUserAccess();
    loadIdentities();
  }, []);

  // Reload assigned roles when the target identity changes (request for someone else)
  useEffect(() => {
    if (selectedIdentityId) {
      loadUserAccess(selectedIdentityId);
    } else {
      loadUserAccess();
    }
  }, [selectedIdentityId]);

  const loadIdentities = async () => {
    try {
      // Only load active (non-orphaned) identities for "request for someone else"
      const res = await API.get('/identities', { params: { limit: 500, orphaned: false } });
      setAllIdentities(res.data.items || []);
    } catch (err) {
      void 0;
    }
  };

  const loadUserAccess = async (targetIdentityId) => {
    try {
      const params = targetIdentityId ? { identity_id: targetIdentityId } : {};
      const res = await API.get('/access-requests/my-role-ids', { params });
      const assignedIds = res.data.assigned_role_ids || [];
      const pendingIds = res.data.pending_role_ids || [];
      setUserRoles(new Set([...assignedIds, ...pendingIds]));
      setPendingRoles(new Set(pendingIds));
    } catch (err) { 
      void 0; 
    }
  };

  const loadSodProtectedRoles = async () => {
    try {
      const res = await API.get('/sod/protected-roles');
      setSodProtectedRoles(new Set(res.data.protected_role_ids || []));
    } catch (err) { void 0; }
  };

  const loadEntitlements = async () => {
    try {
      const res = await API.get('/entitlements', { params: { limit: 200 } });
      setAllEntitlements(res.data.items || []);
    } catch (err) { void 0; }
  };

  const loadRequests = async () => {
    try {
      const res = await API.get('/access-requests', { params: { limit: 100 } });
      setRequests(res.data.items);
    } catch (err) { void 0; }
  };

  const loadCatalog = async () => {
    try {
      const [rolesRes, appsRes] = await Promise.all([
        API.get('/roles', { params: { limit: 200 } }),
        API.get('/applications', { params: { requestable: true, limit: 200 } })
      ]);
      const roles = rolesRes.data.items;
      setAllRoles(roles);
      setCatalog({ 
        roles: roles.filter(r => r.requestable && !r.is_incomplete), 
        applications: appsRes.data.items.filter(a => (a.roles || []).length > 0)
      });
    } catch (err) { void 0; }
  };

  const handleRequestAccess = async (item, type) => {
    if (type === 'role') {
      // Check for SoD conflicts before allowing the request
      setCheckingSod(true);
      try {
        const sodPayload = { role_id: item.id };
        // If requesting for someone else, check SoD against that identity
        if (requestForSomeoneElse && selectedIdentityId) {
          sodPayload.identity_id = selectedIdentityId;
        }
        const res = await API.post('/sod/check-role-request', sodPayload);
        
        if (res.data.is_blocked) {
          // Show SoD blocked dialog with policy details
          setSodConflict(res.data);
          setShowSodBlockedDialog(true);
          setCheckingSod(false);
          return;
        }
        
        // If there are warnings but not blocking, allow but show warning
        if (res.data.has_conflicts) {
          const warningConflicts = res.data.conflicts.filter(c => c.enforcement === 'warn');
          if (warningConflicts.length > 0) {
            toast.warning(
              `Warning: This role may conflict with your existing access (${warningConflicts[0].policy_name}). You can still proceed.`,
              { duration: 5000 }
            );
          }
        }
      } catch (err) {
        void 0;
        // Continue with request if SoD check fails (graceful degradation)
      }
      setCheckingSod(false);
    }
    
    setSelectedItem({ ...item, requestType: type });
    setJustification('');
    setShowRequestDialog(true);
  };

  const handleAppClick = async (app) => {
    setSelectedApp(app);
    setLoadingAppRoles(true);
    setShowAppRolesDialog(true);
    
    try {
      // Get the roles for this application
      const roleIds = app.roles || [];
      const roles = allRoles.filter(r => roleIds.includes(r.id));
      setAppRoles(roles);
    } catch (err) {
      void 0;
      toast.error('Failed to load application roles');
    } finally {
      setLoadingAppRoles(false);
    }
  };

  const handleSelectRoleFromApp = async (role) => {
    // Check for SoD conflicts before allowing the request
    setCheckingSod(true);
    try {
      const sodPayload = { role_id: role.id };
      if (requestForSomeoneElse && selectedIdentityId) {
        sodPayload.identity_id = selectedIdentityId;
      }
      const res = await API.post('/sod/check-role-request', sodPayload);
      
      if (res.data.is_blocked) {
        // Show SoD blocked dialog with policy details
        setSodConflict(res.data);
        setShowAppRolesDialog(false);
        setShowSodBlockedDialog(true);
        setCheckingSod(false);
        return;
      }
      
      // If there are warnings but not blocking, allow but show warning
      if (res.data.has_conflicts) {
        const warningConflicts = res.data.conflicts.filter(c => c.enforcement === 'warn');
        if (warningConflicts.length > 0) {
          toast.warning(
            `Warning: This role may conflict with your existing access (${warningConflicts[0].policy_name}). You can still proceed.`,
            { duration: 5000 }
          );
        }
      }
    } catch (err) {
      void 0;
      // Continue with request if SoD check fails (graceful degradation)
    }
    setCheckingSod(false);
    
    setShowAppRolesDialog(false);
    setSelectedItem({ ...role, requestType: 'role', fromApplication: selectedApp?.name });
    setJustification('');
    setShowRequestDialog(true);
  };

  const submitRequest = async () => {
    try {
      const requestData = {
        type: selectedItem.requestType,
        target_id: selectedItem.id,
        target_name: selectedItem.name,
        justification
      };
      
      // If requesting for someone else, include the identity_id
      if (requestForSomeoneElse && selectedIdentityId) {
        requestData.identity_id = selectedIdentityId;
        const selectedIdentity = allIdentities.find(i => i.id === selectedIdentityId);
        if (selectedIdentity) {
          requestData.identity_name = selectedIdentity.display_name;
        }
      }
      
      await API.post('/access-requests', requestData);
      toast.success(requestForSomeoneElse 
        ? `Access request submitted for ${allIdentities.find(i => i.id === selectedIdentityId)?.display_name || 'selected user'}`
        : 'Access request submitted'
      );
      setShowRequestDialog(false);
      setRequestForSomeoneElse(false);
      setSelectedIdentityId('');
      loadRequests();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to submit';
      // Show SoD violations prominently
      if (err.response?.status === 409) {
        setSodConflict({ message: detail, is_blocked: true, conflicts: [{ policy_name: 'Separation of Duties', enforcement: 'block' }] });
        setShowRequestDialog(false);
        setShowSodBlockedDialog(true);
      } else {
        toast.error(detail);
      }
    }
  };

  const filteredRoles = useMemo(() =>
    catalog.roles.filter(r => r.name.toLowerCase().includes(search.toLowerCase())),
    [catalog.roles, search]
  );
  const filteredApps = useMemo(() =>
    catalog.applications.filter(a => a.name.toLowerCase().includes(search.toLowerCase())),
    [catalog.applications, search]
  );

  const filteredIdentities = useMemo(() =>
    allIdentities
      .filter(i => !identitySearch || i.display_name?.toLowerCase().includes(identitySearch.toLowerCase()) || i.email?.toLowerCase().includes(identitySearch.toLowerCase()))
      .slice(0, 50),
    [allIdentities, identitySearch]
  );

  const showRoleEntitlements = (role, e) => {
    e.stopPropagation();
    setSelectedRoleForEntitlements(role);
    setShowEntitlementsDialog(true);
  };

  const getRoleEntitlementDetails = (role) => {
    const entitlementIds = role.entitlements || [];
    return entitlementIds.map(id => allEntitlements.find(e => e.id === id)).filter(Boolean);
  };

  const statusIcon = (s) => {
    if (s === 'approved') return <CheckCircle size={14} className="text-emerald-500" />;
    if (s === 'rejected') return <XCircle size={14} className="text-red-500" />;
    return <Clock size={14} className="text-amber-500" />;
  };

  const statusClass = (s) => {
    if (s === 'approved') return 'bg-emerald-100 text-emerald-700';
    if (s === 'rejected') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div data-testid="access-requests-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Access Requests</h1>
          <p className="text-sm text-navy-500 mt-1">Request access to roles and applications</p>
        </div>
        <div className="flex gap-2">
          <Button variant={showCatalog ? 'default' : 'outline'} onClick={() => setShowCatalog(true)} className={`rounded-sm text-sm ${showCatalog ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`} data-testid="show-catalog-button">
            Catalog
          </Button>
          <Button variant={!showCatalog ? 'default' : 'outline'} onClick={() => setShowCatalog(false)} className={`rounded-sm text-sm ${!showCatalog ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`} data-testid="show-my-requests-button">
            My Requests
          </Button>
        </div>
      </div>

      {showCatalog ? (
        <>
          <div className="relative max-w-lg mx-auto">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy-400" />
            <Input
              placeholder="Search available roles and applications..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 h-12 text-lg rounded-sm border-slate-200 shadow-sm"
              data-testid="catalog-search-input"
            />
          </div>

          {/* Catalog Tabs */}
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
            <button
              onClick={() => setCatalogTab('all')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${catalogTab === 'all' ? 'bg-orange-500 text-white' : 'text-navy-600 hover:bg-slate-100'}`}
            >
              All ({filteredRoles.length + filteredApps.length})
            </button>
            <button
              onClick={() => setCatalogTab('roles')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${catalogTab === 'roles' ? 'bg-orange-500 text-white' : 'text-navy-600 hover:bg-slate-100'}`}
            >
              <ShieldCheck size={12} className="inline mr-1" />
              Roles ({filteredRoles.length})
            </button>
            <button
              onClick={() => setCatalogTab('apps')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${catalogTab === 'apps' ? 'bg-orange-500 text-white' : 'text-navy-600 hover:bg-slate-100'}`}
            >
              <Cube size={12} className="inline mr-1" />
              Applications ({filteredApps.length})
            </button>
          </div>

          {(catalogTab === 'all' || catalogTab === 'roles') && filteredRoles.length > 0 && (
            <div>
              <h2 className="text-xs font-bold text-navy-500 uppercase tracking-widest mb-3">Available Roles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {filteredRoles.map(role => {
                  const hasAccess = userRoles.has(role.id);
                  const isPending = pendingRoles.has(role.id);
                  return (
                  <div 
                    key={role.id} 
                    className={`bg-white border rounded-sm shadow-sm overflow-hidden transition-shadow ${
                      hasAccess 
                        ? 'border-emerald-200 opacity-75' 
                        : 'border-slate-200 hover:shadow-md'
                    }`} 
                    data-testid={`catalog-role-${role.id}`}
                  >
                    <div className={`h-1.5 ${hasAccess ? (isPending ? 'bg-amber-400' : 'bg-emerald-500') : 'bg-orange-500'}`} />
                    <div className="p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <ShieldCheck size={16} className={hasAccess ? (isPending ? 'text-amber-500 mt-0.5' : 'text-emerald-500 mt-0.5') : 'text-orange-500 mt-0.5'} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className={`font-bold text-sm truncate ${hasAccess ? 'text-navy-600' : 'text-navy-900'}`}>{role.name}</p>
                            {hasAccess && !isPending && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[9px] shrink-0" data-testid={`assigned-badge-${role.id}`}>
                                <Check size={8} className="mr-0.5" /> Assigned
                              </Badge>
                            )}
                            {isPending && (
                              <Badge className="bg-amber-100 text-amber-700 text-[9px] shrink-0" data-testid={`pending-badge-${role.id}`}>
                                <SpinnerGap size={8} className="mr-0.5 animate-spin" /> Pending
                              </Badge>
                            )}
                            {sodProtectedRoles.has(role.id) && (
                              <div className="shrink-0" title="This role is part of a Separation of Duties policy">
                                <Lock size={12} className="text-amber-500" />
                              </div>
                            )}
                          </div>
                          <p className={`text-xs mt-0.5 line-clamp-2 ${hasAccess ? 'text-navy-400' : 'text-navy-500'}`}>{role.description || 'No description'}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <Badge 
                          className="bg-slate-100 text-navy-600 text-[10px] cursor-pointer hover:bg-slate-200 transition-colors"
                          onClick={(e) => showRoleEntitlements(role, e)}
                          data-testid={`role-entitlements-badge-${role.id}`}
                        >
                          <Key size={10} className="mr-0.5" />
                          {(role.entitlements || []).length} entitlements
                        </Badge>
                        {hasAccess ? (
                          <span className={`text-[10px] font-semibold px-3 ${isPending ? 'text-amber-600' : 'text-emerald-600'}`} data-testid={`access-status-${role.id}`}>
                            {isPending ? 'Request pending' : 'Already have access'}
                          </span>
                        ) : (
                          <Button 
                            size="sm" 
                            onClick={() => handleRequestAccess(role, 'role')} 
                            disabled={checkingSod} 
                            className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs h-7 px-3" 
                            data-testid={`request-role-${role.id}`}
                          >
                            {checkingSod ? <SpinnerGap size={12} className="mr-1 animate-spin" /> : <PaperPlaneTilt size={12} className="mr-1" />} Request
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {(catalogTab === 'all' || catalogTab === 'apps') && filteredApps.length > 0 && (
            <div>
              <h2 className="text-xs font-bold text-navy-500 uppercase tracking-widest mb-3">Applications (Browse Roles)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {filteredApps.map(app => (
                  <div key={app.id} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer" data-testid={`catalog-app-${app.id}`} onClick={() => handleAppClick(app)}>
                    <div className="h-1.5 bg-navy-600" />
                    <div className="p-4">
                      <div className="flex items-start gap-2 mb-2">
                        {app.logo_path ? (
                          <div className="w-6 h-6 rounded-sm overflow-hidden bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                            <img 
                              src={getLogoUrl(app.logo_path)} 
                              alt={app.name} 
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                            <div className="hidden w-full h-full items-center justify-center">
                              <Cube size={14} weight="duotone" className="text-navy-600" />
                            </div>
                          </div>
                        ) : (
                          <Cube size={16} weight="duotone" className="text-navy-600 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-bold text-sm text-navy-900 truncate">{app.name}</p>
                          </div>
                          <p className="text-xs text-navy-500 mt-0.5 line-clamp-2">{app.description || 'No description'}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <Badge 
                          className="bg-slate-100 text-navy-600 text-[10px] cursor-pointer hover:bg-slate-200 transition-colors"
                          data-testid={`app-roles-badge-${app.id}`}
                        >
                          <ShieldCheck size={10} weight="bold" className="mr-0.5" />
                          {(app.roles || []).length} roles
                        </Badge>
                        <Button size="sm" className="bg-slate-800 hover:bg-slate-900 text-white rounded-sm text-xs h-7 px-3" data-testid={`view-app-roles-${app.id}`}>
                          View Roles <CaretRight size={12} weight="bold" className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filteredRoles.length === 0 && filteredApps.length === 0 && (
            <div className="text-center py-16 text-sm text-navy-400">
              No requestable items found. Create roles or applications first and mark them as requestable.
            </div>
          )}
        </>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
          <div className="divide-y divide-slate-100">
            {requests.length === 0 ? (
              <EmptyState type="access_requests" />
            ) : requests.map(req => (
              <div key={req.id} className="px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors" data-testid={`request-row-${req.id}`}>
                <div className="flex items-center gap-2">
                  {statusIcon(req.status)}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-navy-900">{req.target_name}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs text-navy-500">{req.type} &middot; {new Date(req.created_at).toLocaleString()}</span>
                    {req.approval_workflow?.length > 0 && req.status === 'pending' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-purple-100 text-purple-700" data-testid={`request-step-${req.id}`}>
                        Step {req.current_approval_step || 1} of {req.approval_workflow.length}
                      </span>
                    )}
                    {req.is_for_someone_else && (
                      <span className="text-[10px] text-blue-600 font-medium">For: {req.identity_name}</span>
                    )}
                  </div>
                  {req.justification && <p className="text-xs text-navy-400 mt-0.5">"{req.justification}"</p>}
                </div>
                <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${statusClass(req.status)}`}>
                  {req.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Application Roles Modal */}
      <Dialog open={showAppRolesDialog} onOpenChange={setShowAppRolesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-3">
                {selectedApp?.logo_path ? (
                  <div className="w-10 h-10 rounded-sm overflow-hidden bg-slate-100 flex items-center justify-center">
                    <img 
                      src={getLogoUrl(selectedApp.logo_path)} 
                      alt={selectedApp.name} 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                    <div className="hidden w-full h-full items-center justify-center">
                      <Cube size={20} className="text-navy-700" />
                    </div>
                  </div>
                ) : (
                  <Cube size={20} className="text-navy-700" />
                )}
                {selectedApp?.name}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <p className="text-xs text-navy-500">{selectedApp.description || 'No description'}</p>
              </div>
              
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-navy-500 mb-3">
                  Select a role to request
                </p>
                
                {loadingAppRoles ? (
                  <div className="flex items-center justify-center py-8">
                    <SpinnerGap className="h-6 w-6 animate-spin text-orange-500" />
                    <span className="ml-2 text-sm text-navy-500">Loading roles...</span>
                  </div>
                ) : appRoles.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {appRoles.map(role => {
                      const hasAccess = userRoles.has(role.id);
                      const isPending = pendingRoles.has(role.id);
                      return (
                      <div 
                        key={role.id} 
                        className={`bg-white border rounded-sm p-3 transition-all ${
                          hasAccess 
                            ? 'border-emerald-200 opacity-75 cursor-default' 
                            : 'border-slate-200 hover:border-orange-300 hover:bg-orange-50 cursor-pointer'
                        }`}
                        onClick={() => !hasAccess && handleSelectRoleFromApp(role)}
                        data-testid={`app-role-${role.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 flex-1">
                            <ShieldCheck size={16} className={`mt-0.5 shrink-0 ${hasAccess ? (isPending ? 'text-amber-500' : 'text-emerald-500') : 'text-orange-500'}`} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className={`font-bold text-sm ${hasAccess ? 'text-navy-600' : 'text-navy-900'}`}>{role.name}</p>
                                {hasAccess && !isPending && (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-[9px] shrink-0">
                                    <Check size={8} className="mr-0.5" /> Assigned
                                  </Badge>
                                )}
                                {isPending && (
                                  <Badge className="bg-amber-100 text-amber-700 text-[9px] shrink-0">
                                    <SpinnerGap size={8} className="mr-0.5 animate-spin" /> Pending
                                  </Badge>
                                )}
                                {sodProtectedRoles.has(role.id) && (
                                  <div title="This role is part of a Separation of Duties policy">
                                    <Lock size={12} className="text-amber-500" />
                                  </div>
                                )}
                              </div>
                              <p className={`text-xs mt-0.5 ${hasAccess ? 'text-navy-400' : 'text-navy-500'}`}>{role.description || 'No description'}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge 
                                  className="bg-amber-100 text-amber-700 text-[10px] cursor-pointer hover:bg-amber-200 transition-colors"
                                  onClick={(e) => showRoleEntitlements(role, e)}
                                  data-testid={`app-role-entitlements-badge-${role.id}`}
                                >
                                  <Key size={8} className="mr-0.5" />{(role.entitlements || []).length} entitlements
                                </Badge>
                              </div>
                            </div>
                          </div>
                          {hasAccess ? (
                            <span className={`text-[10px] font-semibold px-2 py-1 shrink-0 ${isPending ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {isPending ? 'Request pending' : 'Already have access'}
                            </span>
                          ) : (
                            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs h-7 px-3 shrink-0">
                              <PaperPlaneTilt size={12} className="mr-1" /> Request
                            </Button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-navy-400">
                    <ShieldCheck size={32} className="mx-auto mb-2 text-navy-300" />
                    No roles available in this application
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppRolesDialog(false)} className="rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Access Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Request Access</DialogTitle></DialogHeader>
          {selectedItem && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck size={14} className="text-orange-500" />
                  <p className="text-sm font-bold text-navy-900">{selectedItem.name}</p>
                </div>
                <p className="text-xs text-navy-500">{selectedItem.description || 'No description'}</p>
                {selectedItem.fromApplication && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <p className="text-[10px] text-navy-400">
                      From application: <span className="font-semibold text-navy-600">{selectedItem.fromApplication}</span>
                    </p>
                  </div>
                )}
                {(selectedItem.entitlements || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <p className="text-[10px] font-semibold uppercase text-navy-500 mb-1">Included Entitlements</p>
                    <p className="text-xs text-navy-600">{(selectedItem.entitlements || []).length} entitlement(s) will be granted</p>
                  </div>
                )}
                {selectedItem.assignment_duration_days && (
                  <div className="mt-2 pt-2 border-t border-slate-200 bg-amber-50 -mx-3 -mb-3 px-3 py-2 rounded-b-sm">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-amber-600" />
                      <div>
                        <p className="text-xs font-semibold text-amber-800">Time-Limited Access</p>
                        <p className="text-[10px] text-amber-700">
                          This role will be assigned for <span className="font-bold">{selectedItem.assignment_duration_days} days</span>.
                          {' '}You will be notified before expiry to renew.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Request For Selection */}
              <div className="space-y-3">
                <Label className="text-xs font-semibold uppercase tracking-wider">Request For</Label>
                <div className="flex gap-2">
                  <Button
                    variant={!requestForSomeoneElse ? "default" : "outline"}
                    onClick={() => { setRequestForSomeoneElse(false); setSelectedIdentityId(''); }}
                    className={`flex-1 rounded-sm text-sm ${!requestForSomeoneElse ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                    data-testid="request-for-myself-button"
                  >
                    <User size={14} className="mr-1.5" /> Myself
                  </Button>
                  <Button
                    variant={requestForSomeoneElse ? "default" : "outline"}
                    onClick={() => setRequestForSomeoneElse(true)}
                    className={`flex-1 rounded-sm text-sm ${requestForSomeoneElse ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                    data-testid="request-for-someone-else-button"
                  >
                    <Users size={14} className="mr-1.5" /> Someone Else
                  </Button>
                </div>
                
                {requestForSomeoneElse && (
                  <div className="space-y-2">
                    <Popover open={identityPopoverOpen} onOpenChange={setIdentityPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between rounded-sm h-10 font-normal" data-testid="identity-select-trigger">
                          {selectedIdentityId ? (
                            <span className="flex items-center gap-2">
                              <User size={14} className="text-navy-400" />
                              {allIdentities.find(i => i.id === selectedIdentityId)?.display_name || 'Selected'}
                            </span>
                          ) : (
                            <span className="text-navy-400">Search and select an identity...</span>
                          )}
                          <CaretUpDown size={14} className="text-navy-400" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[350px] p-0" align="start">
                        <div className="p-2 border-b border-slate-200">
                          <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy-400" />
                            <Input placeholder="Search by name or email..." value={identitySearch} onChange={(e) => setIdentitySearch(e.target.value)} className="pl-7 h-8 text-sm rounded-sm" data-testid="identity-search-input" />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {filteredIdentities
                            .map(identity => (
                              <div key={identity.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => { setSelectedIdentityId(identity.id); setIdentityPopoverOpen(false); setIdentitySearch(''); }} data-testid={`identity-option-${identity.id}`}>
                                <div className={`w-4 h-4 flex items-center justify-center ${selectedIdentityId === identity.id ? 'text-orange-500' : 'text-transparent'}`}>
                                  <Check size={14} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-navy-700 truncate">{identity.display_name}</p>
                                  {identity.email && <p className="text-xs text-navy-400 truncate">{identity.email}</p>}
                                </div>
                              </div>
                            ))
                          }
                          {filteredIdentities.length === 0 && (
                            <div className="px-3 py-4 text-center text-sm text-navy-400">No identities found</div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {selectedIdentityId && (
                      <div className="bg-blue-50 border border-blue-200 rounded-sm p-2 flex items-center gap-2">
                        <Users size={14} className="text-blue-600" />
                        <p className="text-xs text-blue-700">
                          Requesting access for: <span className="font-semibold">{allIdentities.find(i => i.id === selectedIdentityId)?.display_name}</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Business Justification</Label>
                <Textarea value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Explain why you need this access..." className="mt-1 rounded-sm min-h-[80px]" data-testid="request-justification-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRequestDialog(false); setRequestForSomeoneElse(false); setSelectedIdentityId(''); }} className="rounded-sm">Cancel</Button>
            <Button 
              onClick={submitRequest} 
              disabled={requestForSomeoneElse && !selectedIdentityId}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm disabled:opacity-50" 
              data-testid="submit-request-button"
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SoD Blocked Dialog */}
      <Dialog open={showSodBlockedDialog} onOpenChange={setShowSodBlockedDialog}>
        <DialogContent className="max-w-md" data-testid="sod-blocked-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Prohibit size={20} weight="bold" />
              Access Request Blocked
            </DialogTitle>
          </DialogHeader>
          {sodConflict && (
            <div className="space-y-4 py-2">
              <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                <div className="flex items-start gap-3">
                  <Warning size={24} className="text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">
                      Separation of Duties Violation
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      Requesting <span className="font-bold">"{sodConflict.role_name}"</span> conflicts with your existing access.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-navy-500">Blocking Policy</p>
                {sodConflict.conflicts?.filter(c => c.enforcement === 'block').map((conflict, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck size={14} className="text-red-500" />
                      <span className="font-semibold text-sm text-navy-900">{conflict.policy_name}</span>
                      <Badge className="bg-red-100 text-red-700 text-[10px] ml-auto">
                        {conflict.severity.toUpperCase()}
                      </Badge>
                    </div>
                    {conflict.policy_description && (
                      <p className="text-xs text-navy-500 mb-2">{conflict.policy_description}</p>
                    )}
                    <div className="bg-white border border-red-200 rounded p-2 mt-2">
                      <p className="text-xs text-navy-600">
                        <span className="font-medium">Conflict:</span> You already have 
                        <span className="font-bold text-red-700"> "{conflict.conflicting_role_name}"</span> which cannot be combined with 
                        <span className="font-bold text-red-700"> "{conflict.requested_role_name}"</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">What to do:</span> Contact your manager or an administrator if you believe you need this access. They may grant an exception or help reassign your current roles.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSodBlockedDialog(false)} className="rounded-sm" data-testid="sod-blocked-close-btn">
              Understood
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entitlements Modal */}
      <Dialog open={showEntitlementsDialog} onOpenChange={setShowEntitlementsDialog}>
        <DialogContent className="max-w-md" data-testid="entitlements-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Key size={18} className="text-amber-500" />
                Role Entitlements
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedRoleForEntitlements && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck size={14} className="text-orange-500" />
                  <p className="text-sm font-bold text-navy-900">{selectedRoleForEntitlements.name}</p>
                  {sodProtectedRoles.has(selectedRoleForEntitlements.id) && (
                    <Lock size={12} className="text-amber-500" title="SoD Protected" />
                  )}
                </div>
                <p className="text-xs text-navy-500">{selectedRoleForEntitlements.description || 'No description'}</p>
              </div>
              
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-navy-500 mb-3">
                  Included Entitlements ({(selectedRoleForEntitlements.entitlements || []).length})
                </p>
                
                {getRoleEntitlementDetails(selectedRoleForEntitlements).length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {getRoleEntitlementDetails(selectedRoleForEntitlements).map(ent => (
                      <div key={ent.id} className="bg-white border border-slate-200 rounded-sm p-3">
                        <div className="flex items-start gap-2">
                          <Key size={14} className="text-amber-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-navy-900">{ent.name}</p>
                            <p className="text-xs text-navy-500 mt-0.5">{ent.description || 'No description'}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className="bg-slate-100 text-navy-600 text-[10px]">
                                {ent.type || 'group'}
                              </Badge>
                              {ent.azure_id && (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                                  Azure AD
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-navy-400 border border-dashed border-slate-200 rounded-sm">
                    <Key size={32} className="mx-auto mb-2 text-navy-300" />
                    No entitlements bundled in this role
                  </div>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">Note:</span> Requesting this role will grant you access to all the entitlements listed above.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntitlementsDialog(false)} className="rounded-sm" data-testid="entitlements-close-btn">
              Close
            </Button>
            {selectedRoleForEntitlements && (
              <Button 
                onClick={() => {
                  setShowEntitlementsDialog(false);
                  handleRequestAccess(selectedRoleForEntitlements, 'role');
                }} 
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                data-testid="entitlements-request-btn"
              >
                <PaperPlaneTilt size={14} className="mr-1.5" /> Request This Role
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
